import { useEffect, useState } from "react";
import type { ApiSuccessResponse } from "@erp/shared";
import { apiClient } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";

interface RefreshResponseData {
  user: Parameters<ReturnType<typeof useAuthStore.getState>["setSession"]>[0];
  accessToken: string;
}

/**
 * El refresh token es opaco y de un solo uso (se rota en cada llamada a
 * POST /auth/refresh; ver session.service.ts). Si este efecto se dispara
 * dos veces con el mismo token todavía sin rotar —como hace React 18
 * StrictMode a propósito en desarrollo (monta -> desmonta -> vuelve a
 * montar, para exponer efectos no idempotentes— la segunda llamada
 * llegaría con un token ya invalidado por la primera y fallaría con 401,
 * dejando al usuario en /login pese a tener una sesión válida.
 * Este promise a nivel de módulo (no de componente) asegura que solo se
 * dispare una única solicitud de red real, sin importar cuántas veces se
 * ejecute el efecto.
 */
let inFlightRefresh: Promise<RefreshResponseData | null> | null = null;

function refreshSessionOnce(): Promise<RefreshResponseData | null> {
  if (!inFlightRefresh) {
    inFlightRefresh = apiClient
      .post<ApiSuccessResponse<RefreshResponseData>>("/auth/refresh")
      .then((res) => res.data.data)
      .catch(() => null)
      .finally(() => {
        inFlightRefresh = null;
      });
  }
  return inFlightRefresh;
}

/**
 * Al cargar la aplicación (o recargar la página), el access token en
 * memoria se pierde. Este hook intenta restablecer la sesión usando el
 * refresh token en cookie httpOnly (POST /api/auth/refresh). Si falla,
 * simplemente se asume que no hay sesión activa (no es un error visible).
 */
export function useSessionBootstrap() {
  const setSession = useAuthStore((s) => s.setSession);
  const [isBootstrapping, setIsBootstrapping] = useState(true);

  useEffect(() => {
    let cancelled = false;

    refreshSessionOnce().then((data) => {
      if (cancelled) return;
      if (data) setSession(data.user, data.accessToken);
      setIsBootstrapping(false);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isBootstrapping };
}
